import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowUpRight,
  MapPin,
  KeyRound,
  Wifi,
  ScrollText,
  ChefHat,
  Music,
  Wind,
  Bus,
  Flame,
  LogOut,
  MessageCircle,
  Sparkles,
} from "lucide-react";
import { MEDIA } from "@/content/media";
import { useContent } from "@/lib/ContentProvider";
import { useLanguage } from "@/lib/LanguageProvider";
import { GUIDE_UI } from "@/content/guideUI";
import LanguageToggle from "@/components/LanguageToggle";

/**
 * Guide du Voyageur — accueil (sommaire), bilingue FR / EN.
 *
 * Table des matières éditoriale — lignes horizontales compactes,
 * vignettes carrées, titres, flèches discrètes. La langue se change
 * via un discret sélecteur FR · EN en haut à droite (persistance
 * dans localStorage).
 */

// Groupes par moment du séjour. Chaque item utilise une clé UI (`labelKey`)
// et une clé de sous-titre optionnelle (`subKey`) qui pointent vers
// /content/guideUI.js pour la traduction.
const SECTIONS = [
  {
    key: "avant",
    eyebrowKey: "sectionAvant",
    items: [
      { slug: "adresse",    labelKey: "itemAdresse",     Icon: MapPin,   photo: "porteArrivee" },
      { slug: "code-acces", labelKey: "itemCodeAcces",   Icon: KeyRound, iconImage: "codeAccesIcon" },
      { slug: "transport",  labelKey: "itemTransport",   Icon: Bus },
    ],
  },
  {
    key: "pendant",
    eyebrowKey: "sectionPendant",
    items: [
      { slug: "wifi",          labelKey: "itemWifi",          Icon: Wifi },
      { slug: "bassin",        labelKey: "itemBassin",        Icon: Sparkles,   photo: "bassin",  subKey: "itemBassinSub" },
      { slug: "eclairage",     labelKey: "itemEclairage",     Icon: Music,      subKey: "itemEclairageSub" },
      { slug: "equipements",   labelKey: "itemEquipements",   Icon: ScrollText, photo: "chambre" },
      { slug: "cuisine",       labelKey: "itemCuisine",       Icon: ChefHat,    photo: "cuisine" },
      { slug: "climatisation", labelKey: "itemClimatisation", Icon: Wind },
      { slug: "regles",        labelKey: "itemRegles",        Icon: ScrollText },
    ],
  },
  {
    key: "besoin",
    eyebrowKey: "sectionBesoin",
    items: [
      { slug: "urgences", labelKey: "itemUrgences", Icon: Flame,        subKey: "itemUrgencesSub" },
      { slug: "contact",  labelKey: "itemContact",  Icon: MessageCircle },
    ],
  },
  {
    key: "depart",
    eyebrowKey: "sectionDepart",
    items: [
      { slug: "depart", labelKey: "itemDepart", Icon: LogOut, photo: "jardinEntree" },
    ],
  },
];

// Passerelle externe vers /experiences (pas de duplication)
const BRIDGE_EXPERIENCES = {
  href: "/experiences",
  labelKey: "bridgeExpLabel",
  subKey: "bridgeExpSub",
  photo: MEDIA.experiences.bainDeFleurs,
};

export default function Guide() {
  const navigate = useNavigate();
  const { contact, mediaUrl } = useContent();
  const { lang } = useLanguage();
  const t = (key) => GUIDE_UI[key]?.[lang] ?? GUIDE_UI[key]?.fr ?? "";

  useEffect(() => {
    document.title = GUIDE_UI.docTitleIndex?.[lang] ?? GUIDE_UI.docTitleIndex?.fr ?? "";
  }, [lang]);

  // Titre spécial (avec italique interne)
  const [titleBefore, titleItalic, titleAfter] = GUIDE_UI.title[lang] || GUIDE_UI.title.fr;

  return (
    <div
      data-testid="guide-page"
      className="min-h-screen relative"
      style={{
        background:
          "linear-gradient(180deg, #FBF5EA 0%, #FDF9F0 40%, #FFFFFF 100%)",
      }}
    >
      {/* Papier — grain très subtil */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 opacity-[0.04]"
        style={{
          background:
            "repeating-linear-gradient(0deg, rgba(46,31,24,0.35) 0px, rgba(46,31,24,0) 1px, rgba(46,31,24,0) 3px)",
        }}
      />

      {/* Sélecteur de langue — discret, top right */}
      <LanguageToggle />

      {/* ==== Barre supérieure ==== */}
      <div className="relative w-full max-w-3xl mx-auto px-5 sm:px-8 pt-6 md:pt-8 flex items-center">
        <button
          type="button"
          onClick={() => navigate(-1)}
          data-testid="guide-back"
          className="inline-flex items-center gap-2 font-body text-[11px] tracking-[0.2em] uppercase transition-colors"
          style={{ color: "var(--cr-ink-soft)" }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "var(--cr-ruby)")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "var(--cr-ink-soft)")}
        >
          <ArrowLeft size={14} strokeWidth={1.4} />
          {t("back")}
        </button>
      </div>

      {/* ==== En-tête minimaliste ==== */}
      <header
        data-testid="guide-header"
        className="cr-guide-open relative w-full max-w-3xl mx-auto text-center px-5 sm:px-8 pt-8 md:pt-12 pb-6 md:pb-8"
      >
        <p
          className="font-body text-[10px] tracking-[0.36em] uppercase mb-3"
          style={{ color: "var(--cr-ruby)" }}
        >
          {t("eyebrow")}
        </p>

        <h1
          className="font-display font-light leading-[1.02] tracking-tight"
          style={{
            color: "var(--cr-ink)",
            fontSize: "clamp(2rem, 4.8vw, 3.2rem)",
          }}
        >
          {titleBefore}
          <span className="italic">{titleItalic}</span>
          {titleAfter}
        </h1>

        <p
          className="font-display italic font-light mx-auto mt-3"
          style={{
            color: "var(--cr-ink-soft)",
            fontSize: "clamp(0.95rem, 1.4vw, 1.05rem)",
            lineHeight: 1.5,
            maxWidth: "30rem",
          }}
        >
          {t("subtitle")}
        </p>

        {/* Filet doré fin */}
        <div className="flex items-center justify-center mt-6">
          <span
            className="h-[1px] w-16"
            style={{ background: "var(--cr-gold)", opacity: 0.7 }}
          />
        </div>
      </header>

      {/* ==== Sommaire (2 colonnes desktop) ==== */}
      <main className="relative w-full max-w-3xl mx-auto px-5 sm:px-8 pb-16 md:pb-24">
        {SECTIONS.map((section, sIdx) => (
          <section
            key={section.key}
            data-testid={`guide-section-${section.key}`}
            className="cr-reveal mt-8 md:mt-10 first:mt-0"
            style={{ animationDelay: `${0.12 + sIdx * 0.08}s` }}
          >
            <p
              className="font-body text-[10px] tracking-[0.28em] uppercase mb-3"
              style={{ color: "var(--cr-ruby)" }}
            >
              {t(section.eyebrowKey)}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
              {section.items.map((item, iIdx) => (
                <GuideRow
                  key={item.slug}
                  item={item}
                  label={t(item.labelKey)}
                  sub={item.subKey ? t(item.subKey) : null}
                  index={sIdx * 10 + iIdx}
                  mediaUrl={mediaUrl}
                />
              ))}
            </div>
          </section>
        ))}

        {/* ==== Passerelle vers /experiences (bridge, pas duplication) ==== */}
        <section
          data-testid="guide-section-experiences"
          className="cr-reveal mt-12 md:mt-14"
          style={{ animationDelay: "0.7s" }}
        >
          <p
            className="font-body text-[10px] tracking-[0.28em] uppercase mb-3"
            style={{ color: "var(--cr-ruby)" }}
          >
            {t("sectionGoFurther")}
          </p>
          <Link
            to={BRIDGE_EXPERIENCES.href}
            data-testid="guide-bridge-experiences"
            className="group flex items-center gap-4 py-3.5 pr-3 pl-3 rounded-lg transition-all"
            style={{ borderBottom: "1px solid rgba(184, 153, 104, 0.28)" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(232,184,189,0.06)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            <div
              className="shrink-0 w-11 h-11 rounded-md overflow-hidden"
              style={{ border: "1px solid rgba(139, 37, 48, 0.20)" }}
            >
              <img
                src={mediaUrl ? mediaUrl(BRIDGE_EXPERIENCES.photo) : BRIDGE_EXPERIENCES.photo}
                alt=""
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="flex-1 min-w-0">
              <p
                className="font-display font-light leading-tight truncate"
                style={{ color: "var(--cr-ink)", fontSize: "1.02rem" }}
              >
                {t(BRIDGE_EXPERIENCES.labelKey)}
              </p>
              <p
                className="font-display italic font-light mt-0.5 text-[13px] truncate"
                style={{ color: "var(--cr-ink-soft)" }}
              >
                {t(BRIDGE_EXPERIENCES.subKey)}
              </p>
            </div>
            <ArrowUpRight
              size={16}
              strokeWidth={1.4}
              className="shrink-0 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              style={{ color: "var(--cr-ruby)" }}
            />
          </Link>
        </section>

        {/* ==== Pied — WhatsApp discret ==== */}
        {contact?.whatsappNumber && (
          <footer
            data-testid="guide-footer"
            className="text-center pt-12 md:pt-16"
          >
            <p
              className="font-display italic font-light mb-4"
              style={{ color: "var(--cr-ink-soft)", fontSize: "0.95rem" }}
            >
              {t("footerQuestion")}
            </p>
            <a
              href={`https://wa.me/${contact.whatsappNumber}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="guide-whatsapp"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-body text-[11px] tracking-[0.2em] uppercase transition-all duration-500"
              style={{
                border: "1px solid rgba(139, 37, 48, 0.35)",
                color: "var(--cr-ruby)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "var(--cr-ruby)";
                e.currentTarget.style.color = "#fff";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color = "var(--cr-ruby)";
              }}
            >
              {t("footerWhatsApp")}
              <ArrowUpRight size={12} strokeWidth={1.5} />
            </a>
            {/* Lien discret vers la carte QR imprimable */}
            <div className="mt-6">
              <Link
                to="/guide/qr"
                data-testid="guide-qr-link"
                className="font-body text-[10px] tracking-[0.24em] uppercase transition-colors"
                style={{ color: "var(--cr-ink-soft)", opacity: 0.7 }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "var(--cr-ruby)")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "var(--cr-ink-soft)")}
              >
                {t("footerQRLink")}
              </Link>
            </div>
          </footer>
        )}
      </main>
    </div>
  );
}

/**
 * GuideRow — ligne compacte : vignette carrée + titre (+ sous-titre optionnel)
 * + flèche.
 */
function GuideRow({ item, label, sub, index, mediaUrl }) {
  const { Icon, slug, photo, iconImage } = item;
  const photoUrl = photo ? MEDIA.refuge[photo] : null;
  const iconImageUrl = iconImage ? MEDIA.refuge[iconImage] : null;

  return (
    <Link
      to={`/guide/${slug}`}
      data-testid={`guide-row-${slug}`}
      aria-label={label}
      className="cr-reveal group flex items-center gap-3.5 py-3 pr-2 pl-1 outline-none focus-visible:bg-[color:rgba(232,184,189,0.08)] transition-colors rounded-md"
      style={{
        animationDelay: `${0.18 + index * 0.03}s`,
        borderBottom: "1px solid rgba(184, 153, 104, 0.22)",
      }}
    >
      {/* Vignette carrée : photo (cover), image d'icône (contain) ou icône Lucide */}
      <div
        className="shrink-0 w-10 h-10 rounded-md overflow-hidden flex items-center justify-center transition-transform duration-500 group-hover:-translate-y-0.5"
        style={{
          border: "1px solid rgba(139, 37, 48, 0.18)",
          background: (photoUrl || iconImageUrl) ? "rgba(255, 253, 249, 0.7)" : "rgba(232, 184, 189, 0.10)",
        }}
      >
        {photoUrl ? (
          <img
            src={mediaUrl ? mediaUrl(photoUrl) : photoUrl}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover"
          />
        ) : iconImageUrl ? (
          <img
            src={mediaUrl ? mediaUrl(iconImageUrl) : iconImageUrl}
            alt=""
            loading="lazy"
            className="w-6 h-6 object-contain"
          />
        ) : (
          <Icon
            className="w-[15px] h-[15px]"
            strokeWidth={1.4}
            style={{ color: "var(--cr-ruby)" }}
          />
        )}
      </div>

      {/* Titre + sous-titre optionnel */}
      <div className="flex-1 min-w-0">
        <p
          className="font-display font-light leading-tight truncate transition-colors"
          style={{ color: "var(--cr-ink)", fontSize: "0.98rem" }}
        >
          {label}
        </p>
        {sub && (
          <p
            className="font-display italic font-light mt-0.5 truncate"
            style={{ color: "var(--cr-ink-soft)", fontSize: "0.78rem" }}
          >
            {sub}
          </p>
        )}
      </div>

      {/* Flèche */}
      <ArrowUpRight
        size={14}
        strokeWidth={1.4}
        className="shrink-0 transition-all duration-500 opacity-40 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
        style={{ color: "var(--cr-ruby)" }}
      />
    </Link>
  );
}
